import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import notify from 'devextreme/ui/notify';
import {
  clearDashboardSessionStorage,
  isStoredAccessTokenValid,
} from './dashboard-session';

export type DashboardRole =
  | 'Customer'
  | 'Seller'
  | 'WebsiteAdmin'
  | 'Admin'
  | 'Driver'
  | 'DriverAdmin'
  | 'CustomerSupport'
  | 'CustomerSupportAdmin'
  | 'Accountant';

interface StoredUser {
  roles?: unknown;
}

function getStoredUser(): StoredUser | null {
  try {
    return JSON.parse(
      globalThis.sessionStorage?.getItem('user') ?? 'null',
    ) as StoredUser | null;
  } catch {
    return null;
  }
}

function getUserRoles(user: StoredUser): string[] {
  return Array.isArray(user.roles)
    ? user.roles.filter((role): role is string => typeof role === 'string')
    : [];
}

export function dashboardRoleGuard(
  requiredRole: DashboardRole,
  loginPath: string[],
): CanActivateFn {
  return (_, state) => {
    const router = inject(Router);
    const user = getStoredUser();
    if (!isStoredAccessTokenValid() || !user) {
      clearDashboardSessionStorage();
      return router.createUrlTree(loginPath, {
        queryParams: { returnUrl: state.url },
      });
    }

    if (getUserRoles(user).includes(requiredRole)) {
      return true;
    }

    clearDashboardSessionStorage();

    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      notify(
        inject(TranslateService).instant(
          "You don't have enough permissions to access this dashboard.",
        ),
        'error',
        5000,
      );
    }

    return router.createUrlTree(loginPath);
  };
}

export function dashboardGuestGuard(
  requiredRole: DashboardRole,
  dashboardPath: string[],
): CanActivateFn {
  return () => {
    const user = getStoredUser();

    if (
      isStoredAccessTokenValid() &&
      user &&
      getUserRoles(user).includes(requiredRole)
    ) {
      return inject(Router).createUrlTree(dashboardPath);
    }

    // Do not send another dashboard's bearer token when retrying credentials.
    clearDashboardSessionStorage();
    return true;
  };
}
